import { FunctionComponent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { CompanyOtherList } from "@signalist/shared-types";

import { Tooltip } from "@/components/Tooltip/Tooltip";

/** How many lists the cell names inline before it only counts them. */
export const INLINE_LIST_NAMES = 2;

interface CompanyOtherListsCellProps {
	/** `null`/absent: the lead has no organization, so its company cannot be matched. */
	lists: CompanyOtherList[] | null | undefined;
}

/**
 * "Company was on other lists" (PREC-2750): how many other lists of the workspace hold a lead of
 * this lead's company, the strongest ones named inline, all of them linked in the tooltip.
 */
export const CompanyOtherListsCell: FunctionComponent<CompanyOtherListsCellProps> = ({ lists }) => {
	const { t } = useTranslation();

	if (!lists) {
		return (
			<Tooltip content={t("inbox.companyOtherListsUnknown")}>
				<span className="text-text-meta" data-testid="company-other-lists-unknown">
					—
				</span>
			</Tooltip>
		);
	}

	if (lists.length === 0) {
		return <span className="text-text-meta">{t("inbox.companyOtherListsNone")}</span>;
	}

	const inlineNames = lists
		.slice(0, INLINE_LIST_NAMES)
		.map((list) => list.listName)
		.join(", ");
	const hiddenCount = lists.length - INLINE_LIST_NAMES;

	return (
		<Tooltip
			panel
			content={
				<div className="flex flex-col gap-1">
					<div className="text-xs font-semibold text-text-meta">
						{t("inbox.companyOtherListsHeader", { count: lists.length })}
					</div>
					<ul className="flex flex-col gap-1">
						{lists.map((list) => (
							<li key={list.listId} className="flex items-center justify-between gap-3">
								<Link
									to={`/app/lists/detail/${list.listId}`}
									target="_blank"
									className="truncate text-sm text-text hover:underline"
								>
									{list.listName}
								</Link>
								<span className="shrink-0 text-xxs text-text-meta">
									{t("inbox.companyOtherListsLeads", { count: list.leadCount })}
								</span>
							</li>
						))}
					</ul>
				</div>
			}
		>
			<div className="flex items-center gap-2 whitespace-nowrap">
				<span className="font-semibold" data-testid="company-other-lists-count">
					{lists.length}
				</span>
				<span className="truncate max-w-[240px] text-text-meta">
					{inlineNames}
					{hiddenCount > 0 ? ` +${hiddenCount}` : ""}
				</span>
			</div>
		</Tooltip>
	);
};
